"use client";

import { useMemo, useState } from "react";
import {
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  CARD,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { PowerChart } from "@/components/tools/stats-distributions/charts";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import { lineGeometry } from "@/lib/tool-kit/stats-distributions/chart";
import type { NumberFormatter } from "@/lib/tool-kit/stats-distributions/format";
import {
  curveLimit,
  MIN_N,
  type PowerDesign,
  type PowerForm,
  type PowerFormProblem,
  type PowerProblem,
  powerCurve,
  type SolvedPower,
  solvePower,
} from "@/lib/tool-kit/stats-distributions/power";

/** English strings. A translated content file overrides these through `ui`. */
export const POWER_ANALYSIS_CALCULATOR_STRINGS = {
  testLabel: "Test",
  testIndependent: "Two independent means (t-test)",
  testPaired: "Paired or one-sample mean (t-test)",
  testProportions: "Two independent proportions",
  solveLabel: "Solve for",
  solveN: "Sample size",
  solvePower: "Power",
  effectLabel: "Effect size (Cohen's d)",
  effectHint:
    "Base d on earlier studies or the smallest difference that would matter. Cohen's 0.2 / 0.5 / 0.8 are a last resort.",
  presetSmall: "Small (0.2)",
  presetMedium: "Medium (0.5)",
  presetLarge: "Large (0.8)",
  p1Label: "Expected proportion, group 1 (p₁)",
  p2Label: "Expected proportion, group 2 (p₂)",
  alphaLabel: "Significance level (α)",
  tailsLabel: "Tails",
  twoTailed: "Two-tailed",
  oneTailed: "One-tailed",
  targetLabel: "Target power (1 − β)",
  nLabel: "Sample size per group",
  pairsLabel: "Number of pairs (or participants)",
  results: "Results",
  perGroup: "Per group",
  totalSample: "Total sample size",
  pairsNeeded: "Pairs (participants)",
  achievedPower: "Power achieved",
  power: "Power",
  criticalT: "Critical t on {df} df",
  criticalZ: "Critical z",
  noncentralityT: "Noncentrality δ",
  noncentralityZ: "h × √(n/2)",
  cohensH: "Cohen's h",
  perGroupCount:
    "one: {count} participant per group|other: {count} participants per group",
  pairCount: "one: {count} pair|other: {count} pairs",
  summary:
    "With {count}, a {tails} {test} at α = {alpha} has {power}% power to detect an effect of {effect}.",
  testNameIndependent: "independent-samples t-test",
  testNamePaired: "paired t-test",
  testNameProportions: "two-proportion z-test",
  twoTailedWord: "two-tailed",
  oneTailedWord: "one-tailed",
  oneTailedNote:
    "One-tailed power assumes the true effect lies in the direction you predicted; an effect the other way would be missed entirely.",
  curveHeading: "Power curve",
  curveLabel: "Power plotted against sample size, with the target power marked",
  curveX: "Sample size per group",
  curveXPaired: "Number of pairs",
  curveY: "Power",
  tooLarge:
    "No sample below 10 million per group reaches that power — the effect is too small to detect in practice.",
  enterEffect: "Enter a non-zero effect size.",
  enterProportions: "Enter two different proportions between 0 and 1.",
  enterAlpha: "Enter α between 0 and 1, such as 0.05.",
  enterTarget: "Enter a target power between α and 1, such as 0.80.",
  enterN: "Enter a whole-number sample size of at least 2.",
} as const;

type Strings = typeof POWER_ANALYSIS_CALCULATOR_STRINGS;

type Form = PowerForm;

const INITIAL: Form = {
  design: "independent",
  solve: "n",
  d: "0.5",
  p1: "0.50",
  p2: "0.35",
  alpha: "0.05",
  tails: 2,
  target: "0.80",
  n: "30",
};

const PRESETS = ["0.2", "0.5", "0.8"] as const;

const PROBLEMS: Record<PowerFormProblem, keyof Strings> = {
  effect: "enterEffect",
  proportions: "enterProportions",
  alpha: "enterAlpha",
  target: "enterTarget",
  n: "enterN",
};

function EffectInputs({
  form,
  update,
  strings,
}: {
  form: Form;
  update: (patch: Partial<Form>) => void;
  strings: Strings;
}) {
  if (form.design === "proportions") {
    return (
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="pa-p1"
          label={strings.p1Label}
          numeric
          onChange={(p1) => update({ p1 })}
          value={form.p1}
        />
        <TextField
          id="pa-p2"
          label={strings.p2Label}
          numeric
          onChange={(p2) => update({ p2 })}
          value={form.p2}
        />
      </div>
    );
  }
  const labels = [
    strings.presetSmall,
    strings.presetMedium,
    strings.presetLarge,
  ];
  return (
    <div className="space-y-2">
      <div className="max-w-xs">
        <TextField
          id="pa-d"
          label={strings.effectLabel}
          numeric
          onChange={(d) => update({ d })}
          value={form.d}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        {PRESETS.map((preset, index) => (
          <button
            className={BUTTON_SMALL}
            key={preset}
            onClick={() => update({ d: preset })}
            type="button"
          >
            {labels[index]}
          </button>
        ))}
      </div>
      <p className={HINT}>{strings.effectHint}</p>
    </div>
  );
}

function SettingsInputs({
  form,
  update,
  strings,
}: {
  form: Form;
  update: (patch: Partial<Form>) => void;
  strings: Strings;
}) {
  const paired = form.design === "paired";
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <TextField
        id="pa-alpha"
        label={strings.alphaLabel}
        numeric
        onChange={(alpha) => update({ alpha })}
        value={form.alpha}
      />
      <SelectField
        id="pa-tails"
        label={strings.tailsLabel}
        onChange={(value) => update({ tails: value === "1" ? 1 : 2 })}
        options={[
          { value: "2", label: strings.twoTailed },
          { value: "1", label: strings.oneTailed },
        ]}
        value={String(form.tails)}
      />
      {form.solve === "n" ? (
        <TextField
          id="pa-target"
          label={strings.targetLabel}
          numeric
          onChange={(target) => update({ target })}
          value={form.target}
        />
      ) : (
        <TextField
          id="pa-n"
          label={paired ? strings.pairsLabel : strings.nLabel}
          numeric
          onChange={(n) => update({ n })}
          value={form.n}
        />
      )}
    </div>
  );
}

function effectText(problem: PowerProblem, fmt: NumberFormatter): string {
  const symbol = problem.design === "proportions" ? "h" : "d";
  return `${symbol} = ${fmt(Math.abs(problem.effect), { maximumFractionDigits: 3 })}`;
}

function Summary({
  form,
  outcome,
  strings,
  fmt,
}: {
  form: Form;
  outcome: SolvedPower;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const locale = useToolLocale();
  const { problem, n, point } = outcome;
  const paired = problem.design === "paired";
  const count = tPlural(
    paired ? strings.pairCount : strings.perGroupCount,
    locale,
    n,
    { count: fmt(n) }
  ).trim();
  const testNames: Record<PowerDesign, string> = {
    independent: strings.testNameIndependent,
    paired: strings.testNamePaired,
    proportions: strings.testNameProportions,
  };
  return (
    <div className={`${RESULT_CARD} space-y-2 text-foreground text-sm`}>
      <p>
        {t(strings.summary, {
          count,
          tails:
            form.tails === 2 ? strings.twoTailedWord : strings.oneTailedWord,
          test: testNames[problem.design],
          alpha: fmt(problem.alpha, { maximumFractionDigits: 4 }),
          power: fmt(point.power * 100, { maximumFractionDigits: 1 }),
          effect: effectText(problem, fmt),
        })}
      </p>
      {form.tails === 1 ? (
        <p className="text-muted-foreground">{strings.oneTailedNote}</p>
      ) : null}
    </div>
  );
}

function Curve({
  outcome,
  target,
  strings,
  fmt,
}: {
  outcome: SolvedPower;
  target: number | null;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const { problem, n, point } = outcome;
  const geometry = useMemo(() => {
    const nMax = curveLimit(n);
    const points = powerCurve(problem, nMax).map((p) => ({
      x: p.n,
      y: p.power,
    }));
    return lineGeometry(points, { xMin: MIN_N, xMax: nMax, yMin: 0, yMax: 1 });
  }, [problem, n]);
  return (
    <div className={`${CARD} space-y-3`}>
      <h3 className={WIDGET_H3}>{strings.curveHeading}</h3>
      <PowerChart
        format={(value) => fmt(value, { maximumFractionDigits: 1 })}
        geometry={geometry}
        label={strings.curveLabel}
        marker={{ n, power: point.power }}
        target={target}
        xLabel={
          problem.design === "paired" ? strings.curveXPaired : strings.curveX
        }
        yLabel={strings.curveY}
      />
    </div>
  );
}

function Results({
  form,
  outcome,
  strings,
  fmt,
}: {
  form: Form;
  outcome: SolvedPower;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const { problem, n, point } = outcome;
  const paired = problem.design === "paired";
  const proportions = problem.design === "proportions";
  const solvingN = form.solve === "n";
  const four = (value: number) =>
    fmt(value, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis={solvingN}
          label={paired ? strings.pairsNeeded : strings.perGroup}
          value={fmt(n)}
        />
        {paired ? null : (
          <StatCard label={strings.totalSample} value={fmt(n * 2)} />
        )}
        <StatCard
          emphasis={!solvingN}
          label={solvingN ? strings.achievedPower : strings.power}
          value={four(point.power)}
        />
        <StatCard
          label={
            point.df === undefined
              ? strings.criticalZ
              : t(strings.criticalT, { df: fmt(point.df) })
          }
          value={four(point.critical)}
        />
        <StatCard
          label={proportions ? strings.noncentralityZ : strings.noncentralityT}
          value={four(point.noncentrality)}
        />
        {proportions ? (
          <StatCard label={strings.cohensH} value={four(problem.effect)} />
        ) : null}
      </StatGrid>
      <Summary fmt={fmt} form={form} outcome={outcome} strings={strings} />
      <Curve
        fmt={fmt}
        outcome={outcome}
        strings={strings}
        target={solvingN ? parseNumberInput(form.target) : null}
      />
    </>
  );
}

export default function PowerAnalysisCalculatorWidget() {
  const strings = useToolUi(POWER_ANALYSIS_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [form, setForm] = useState<Form>(INITIAL);
  const update = (patch: Partial<Form>) =>
    setForm((current) => ({ ...current, ...patch }));
  const outcome = useMemo(() => solvePower(form), [form]);

  return (
    <>
      <section className="space-y-5">
        <PillGroup
          label={strings.testLabel}
          name="pa-design"
          onChange={(design) => update({ design })}
          options={[
            { value: "independent", label: strings.testIndependent },
            { value: "paired", label: strings.testPaired },
            { value: "proportions", label: strings.testProportions },
          ]}
          value={form.design}
        />
        <PillGroup
          label={strings.solveLabel}
          name="pa-solve"
          onChange={(value) => update({ solve: value })}
          options={[
            { value: "n", label: strings.solveN },
            { value: "power", label: strings.solvePower },
          ]}
          value={form.solve}
        />
        <EffectInputs form={form} strings={strings} update={update} />
        <SettingsInputs form={form} strings={strings} update={update} />
      </section>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {outcome.kind === "problem" ? (
          <Notice>{strings[PROBLEMS[outcome.problem]]}</Notice>
        ) : null}
        {outcome.kind === "tooLarge" ? (
          <Notice>{strings.tooLarge}</Notice>
        ) : null}
        {outcome.kind === "ok" ? (
          <Results fmt={fmt} form={form} outcome={outcome} strings={strings} />
        ) : null}
      </section>
    </>
  );
}
